import { Suspense, useState } from 'react';
import {
  type GqlStripeCustomerQuery,
  type GqlStripePmCard,
  type GqlStripePmLink,
  StripePaymentMethodsDocument,
  useStripeCreateSetupCheckoutSessionMutation,
  useStripeDeletePaymentMethodMutation,
  useStripePaymentMethodsSuspenseQuery,
  useStripeSetDefaultPaymentMethodMutation,
} from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { List } from '@startuptools/ui/List';
import { ListItem } from '@startuptools/ui/ListItem';
import { ListItemDecorator } from '@startuptools/ui/ListItemDecorator';
import { Avatar } from '@startuptools/ui/Avatar';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Typography } from '@startuptools/ui/Typography';
import { ListItemContent } from '@startuptools/ui/ListItemContent';
import { Button } from '@startuptools/ui/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@startuptools/ui/IconButton';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Chip } from '@startuptools/ui/Chip';
import { Link } from '@startuptools/ui/Link';
import { Trans } from '@lingui/react';
import { loadStripe } from '@stripe/stripe-js';
import { stripePublicKey } from '../../../environments/environment';
import LinkIcon from '../../../assets/card-icons/link.svg';
import VisaIcon from '../../../assets/card-icons/visa.svg';
import AmexIcon from '../../../assets/card-icons/amex.svg';
import MastercardIcon from '../../../assets/card-icons/mastercard.svg';
import { BillingDocument } from './Billing.graphql';

const CardIcon = ({ brand }: { brand: string }) => {
  switch (brand) {
    case 'visa': {
      return <VisaIcon />;
    }
    case 'mastercard': {
      return <MastercardIcon />;
    }
    case 'amex': {
      return <AmexIcon />;
    }
    default: {
      return <CreditCardIcon />;
    }
  }
};

const PmCard = ({ card }: { card: GqlStripePmCard }) => {
  return (
    <>
      <ListItemDecorator>
        <Avatar>
          <CardIcon brand={card.brand} />
        </Avatar>
      </ListItemDecorator>
      <ListItemContent sx={{ paddingX: 2 }}>
        <Typography level="title-sm" sx={{ textTransform: 'capitalize' }}>
          {card.brand} - **** {card.last4}
        </Typography>
        <Typography level="body-sm">
          <Trans id="expires:" /> {card.exp_month}/{card.exp_year}
        </Typography>
      </ListItemContent>
    </>
  );
};

const PmLink = ({ link }: { link: GqlStripePmLink }) => {
  return (
    <>
      <ListItemDecorator>
        <Avatar>
          <LinkIcon />
        </Avatar>
      </ListItemDecorator>
      <ListItemContent sx={{ paddingX: 2 }}>
        <Typography level="title-sm">{link.email}</Typography>
        <Typography level="body-sm">
          <Link href="https://app.link.com" target="_href">
            <Trans id="Click here to handle Link account" />
          </Link>
        </Typography>
      </ListItemContent>
    </>
  );
};

const PaymentMethodsInner = ({
  stripeCustomer,
}: {
  stripeCustomer: NonNullable<GqlStripeCustomerQuery['stripeCustomer']>;
}) => {
  const companyId = useCompanyId();
  const [loadingSetup, setLoadingSetup] = useState(false);
  const {
    data: { stripePaymentMethods: pms },
  } = useStripePaymentMethodsSuspenseQuery({ variables: { companyId } });
  const [doSetDefault, { loading: loadingSetDefault }] = useStripeSetDefaultPaymentMethodMutation({
    awaitRefetchQueries: true,
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doAddPaymentMethod] = useStripeCreateSetupCheckoutSessionMutation({
    variables: { companyId, input: { cancelUrl: window.location.href, successUrl: window.location.href } },
    awaitRefetchQueries: true,
  });
  const [doDelete, { loading: loadingDelete }] = useStripeDeletePaymentMethodMutation({
    refetchQueries: [StripePaymentMethodsDocument, BillingDocument],
    awaitRefetchQueries: true,
  });

  const handleDoSetDefault = async (pmId: string) => {
    await doSetDefault({ variables: { companyId, pmId } });
  };

  const handleAddPaymentMethod = async () => {
    setLoadingSetup(true);
    const { data, errors } = await doAddPaymentMethod();
    if (errors?.length || !data) {
      setLoadingSetup(false);
      return;
    }
    const stripe = await loadStripe(stripePublicKey);
    if (!stripe) {
      setLoadingSetup(false);
      return;
    }
    await stripe?.redirectToCheckout({ sessionId: data.stripeCreateSetupCheckoutSession.id });
  };

  const handleDeletePm = async (pmId: string) => {
    await doDelete({ variables: { companyId, pmId } });
  };

  const loading = loadingSetup || loadingSetDefault || loadingDelete;

  return (
    <Stack gap={1}>
      <Typography level="h4">
        <Trans id="Payment Methods" />
      </Typography>
      <Box>
        <Button
          variant="outlined"
          onClick={handleAddPaymentMethod}
          loading={loadingSetup}
          disabled={!loadingSetup && loading}
        >
          <Trans id="Add Payment Method" />
        </Button>
      </Box>
      <List>
        {pms
          .filter(pm => pm.card || pm.link)
          .map(pm => (
            <ListItem
              key={pm.id}
              endAction={
                <Stack direction="row" gap={1}>
                  {pm.id === stripeCustomer.invoice_settings.default_payment_method ? (
                    <Chip size="sm" color="success" variant="outlined">
                      <Trans id="Default" />
                    </Chip>
                  ) : (
                    <Button
                      variant="outlined"
                      color="neutral"
                      size="sm"
                      onClick={() => handleDoSetDefault(pm.id)}
                      disabled={loading}
                    >
                      <Trans id="Make Default" />
                    </Button>
                  )}
                  {pms.length > 1 && (
                    <IconButton color="danger" disabled={loading} onClick={() => handleDeletePm(pm.id)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              }
            >
              {pm.card && <PmCard card={pm.card} />}
              {pm.link && <PmLink link={pm.link} />}
            </ListItem>
          ))}
      </List>
    </Stack>
  );
};

export const PaymentMethods = ({
  stripeCustomer,
}: {
  stripeCustomer: NonNullable<GqlStripeCustomerQuery['stripeCustomer']>;
}) => {
  return (
    <Suspense>
      <PaymentMethodsInner stripeCustomer={stripeCustomer} />
    </Suspense>
  );
};
